import { api } from './api';

/**
 * INCPT presale — TMA ↔ `/v1/presale`. Token is off-chain until listing;
 * purchases are paid with USDT on TRC-20 to the configured treasury.
 */

export interface PresaleRound {
  /** Ticker being sold — "INCPT". */
  symbol: string;
  /** USDT paid per token. */
  price: number;
  /** Minimum participation in USD. */
  minAmount: number;
  /** Reference market price, for the "instant upside" tile. */
  marketPrice: number;
  /** 24h move of the reference price, in percent. */
  marketChange24h: number;
  /** False once the round stops accepting money. */
  open: boolean;
  /** Networks the token will list on — display only. */
  networks: PresaleNetwork[];
  /** USDT TRC-20 destination for payments (null if admin has not set it). */
  treasuryTrx: string | null;
}

export interface PresaleNetwork {
  id: string;
  label: string;
  short: string;
}

export interface PresaleTokenStats {
  marketCap: number;
  volume24h: number;
  createdYear: number;
}

export interface PresalePurchase {
  id: string;
  /** USD paid. */
  amount: number;
  /** Tokens credited — amount / price at purchase time. */
  tokens: number;
  /** Price locked for this purchase. */
  price: number;
  createdAt: string;
  status: 'settled';
}

export interface PresaleOverview {
  /** False when the round is off and the user is not on the TG allowlist. */
  visible: boolean;
  round: PresaleRound;
  purchases: PresalePurchase[];
  /** Exact off-chain INCPT balance (decimal string — do not Number() for sends). */
  balance: string;
  priceHistory: [number, number][];
  stats: PresaleTokenStats;
}

export const fetchPresale = (initData: string) => api<PresaleOverview>(initData, '/v1/presale');

export const buyPresale = (
  initData: string,
  body: { payment_transfer_id: string },
  idempotencyKey: string,
) =>
  api<{ purchase: PresalePurchase }>(initData, '/v1/presale/purchases', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });

export type PresaleErrorCode =
  | 'amount_below_min'
  | 'round_closed'
  | 'amount_too_large'
  | 'payment_not_found'
  | 'payment_not_confirmed'
  | 'payment_wrong_token'
  | 'payment_wrong_recipient'
  | 'payment_already_used'
  | 'presale_treasury_not_configured'
  | 'insufficient_balance';

export function presaleErrorMessage(
  raw: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  fallback: string,
): string {
  const has = (code: PresaleErrorCode) => raw.includes(code);
  if (has('amount_below_min')) return t('presale.errMin');
  if (has('round_closed')) return t('presale.errClosed');
  if (has('amount_too_large')) return t('presale.errTooLarge');
  if (has('presale_treasury_not_configured')) return t('presale.errTreasury');
  if (has('insufficient_balance')) return t('presale.errBalance');
  if (has('payment_already_used')) return t('presale.errUsed');
  if (
    has('payment_not_confirmed') ||
    has('payment_not_found') ||
    has('payment_wrong_token') ||
    has('payment_wrong_recipient')
  ) {
    return t('presale.errPayment');
  }
  return fallback;
}

export const formatPrice = (n: number) => n.toFixed(2);

export const tokensFor = (amount: number, round: PresaleRound) =>
  round.price > 0 ? amount / round.price : 0;

/** Presale discount vs reference price: (ref − price) / ref. */
export const discountPercent = (round: PresaleRound) =>
  round.marketPrice > 0 ? Math.round((1 - round.price / round.marketPrice) * 100) : 0;
